import { nullSafeToString } from '../lib/util';

/*
 * The storefront download handlers these files are served through: TBGStorePrintFileDownloadHandler and
 * TBGStoreProofFileDownloadHandler, whose URL_PATTERNs these are. Both authorize the session user
 * against the file and then redirect to an expiring resource url, so a link is only good for the
 * shopper it was built for.
 */

const DOWNLOAD_PREFIXES = {
    PRINT: '/print-file',
    PROOF: '/proof-file',
};

/*
 * RegularOrderItemV2Provider#buildLink emits "/{id}" and nothing else, and only when the file has moved
 * to offsite storage or an offsite request has failed - an ordinary on-site file arrives with a null
 * link. So link alone cannot tell a consumer where to download anything, which is why every theme that
 * renders these files has reimplemented this same test against a JSP-injected url prefix. The
 * downloadUrl below is that derivation done once.
 */

const OFFSITE_LINK = /^\/[0-9]+$/u;

function toDownloadUrl (link, type) {
    if (!link) {
        return null;
    }

    return OFFSITE_LINK.test(link) ? `${DOWNLOAD_PREFIXES[type]}${link}` : link;
}

function toDate (value) {
    return value ? Object.freeze(new Date(value)) : null;
}

/**
 * A file attached to a job on a submitted {@link Order Order} - either the artwork the shopper supplied
 * or a proof Collaterate produced from it.
 *
 * Both kinds come back in the same shape and are told apart by `type`. A proof file never carries a
 * `note`, and `isExternallyManaged` is meaningful only on a print file.
 *
 * @property {number} id - (Read only) This file's unique id.
 * @property {string} type - (Read only) `"PRINT"` for shopper artwork, `"PROOF"` for a proof produced from it.
 * @property {string} name - (Read only) The file name.
 * @property {?string} downloadUrl - (Read only) Where to send the shopper to download this file. Null when the file is not downloadable - see {@link OrderedItemFile#link link}.
 * @property {?string} link - (Read only) The raw `link` the API returned, `"/{id}"` or null. Prefer {@link OrderedItemFile#downloadUrl downloadUrl}, which resolves this against the right storefront handler.
 * @property {string} note - (Read only) The note recorded against this file. Always empty on a proof file.
 * @property {string} vaultState - (Read only) `"NORMAL"`, `"VAULTING"` or `"VAULTED"`. A vaulted file has been moved to secure storage and can no longer be downloaded, whatever downloadUrl says.
 * @property {boolean} isActive - (Read only) Whether this file is the one being used for the job. A job can hold several files with only one active.
 * @property {boolean} isStoredOffsite - (Read only) Whether the file has been moved to offsite storage. An offsite file may need retrieval before it can be downloaded, which the download handler starts and reports.
 * @property {boolean} isExternallyManaged - (Read only) Whether the file is managed outside Collaterate. Always false on a proof file.
 * @property {?Date} createdOn - (Read only) When the file was uploaded.
 * @property {?Date} updatedOn - (Read only) When the file was last changed.
 */

class OrderedItemFile {
    constructor (data = {}, type = 'PRINT') {
        this.id = data.id;
        this.type = type;

        this.name = nullSafeToString(data.name);
        this.link = data.link || null;
        this.downloadUrl = toDownloadUrl(this.link, type);
        this.note = nullSafeToString(data.note);
        this.vaultState = nullSafeToString(data.vaultState);

        this.isActive = Boolean(data.active);
        this.isStoredOffsite = Boolean(data.storedOffsite);
        this.isExternallyManaged = Boolean(data.externallyManaged);

        this.createdOn = toDate(data.createdOn);
        this.updatedOn = toDate(data.updatedOn);

        Object.freeze(this);
    }
}

export default OrderedItemFile;
